import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  useProgramMembers, 
  useProgramApplicants, 
  useProgramFollowers,
  useProgramMutations 
} from '../../../hooks/useProgramsApi';

/**
 * Consolidated hook for fetching and managing all people data
 * Combines: Members, Staff, Applicants, Waitlist, Followers
 */
export const usePeopleData = (programId, clubId, forceEnable = false) => {
  const [clubMembers, setClubMembers] = useState([]);
  const [clubMembersLoading, setClubMembersLoading] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all', // all, members, staff, applicants, waitlist, followers
    search: '',
    role: '',
    status: ''
  });
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Fetch program members using existing hook
  const { 
    data: members, 
    refetch: refetchMembers, 
    loading: membersLoading,
    error: membersError 
  } = useProgramMembers(programId);

  // Fetch applicants using existing hook
  const { 
    data: applicants, 
    refetch: refetchApplicants, 
    loading: applicantsLoading,
    error: applicantsError 
  } = useProgramApplicants(programId, forceEnable, { review_status: 'Pending', limit: 50 });

  // Fetch followers using existing hook
  const { 
    data: followers, 
    refetch: refetchFollowers,
    loading: followersLoading 
  } = useProgramFollowers(programId, forceEnable, { limit: 50 });

  // Mutations
  const mutations = useProgramMutations(programId);

  // Fetch club managerial users
  const fetchClubMembers = useCallback(async () => {
    if (!clubId) return;
    setClubMembersLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      const response = await fetch(`${backendUrl}/api/clubs/${clubId}/members`);
      if (response.ok) {
        const data = await response.json();
        // Filter for managerial roles only
        const managerialUsers = data.filter(member => 
          ['creator', 'owner', 'manager'].includes(member.role?.toLowerCase())
        );
        setClubMembers(managerialUsers);
      }
    } catch (error) {
      console.error('Error fetching club members:', error);
      setClubMembers([]);
    } finally {
      setClubMembersLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    fetchClubMembers();
  }, [fetchClubMembers]);

  // Normalize data into unified format
  const normalizedData = useMemo(() => {
    const result = [];

    // Add club management members
    clubMembers.forEach(member => {
      result.push({
        id: member._id || member.userId,
        uniqueKey: `club-${member._id || member.userId}`,
        type: 'club-management',
        user: member.user || member,
        role: member.role,
        status: 'Active',
        source: 'club',
        joinedAt: member.createdAt,
        canEdit: false, // Club roles managed elsewhere
        _original: member
      });
    });

    // Add program members (excluding waitlisted)
    (members || []).filter(m => m.status !== 'Waitlisted').forEach(member => {
      const isStaff = !['Player', 'Observer'].includes(member.role);
      result.push({
        id: member._id || member.userId,
        uniqueKey: `member-${member._id || member.userId}`,
        type: isStaff ? 'staff' : 'member',
        user: member.user || { _id: member.userId },
        role: member.role,
        status: member.status,
        source: 'program',
        joinedAt: member.createdAt,
        teamId: member.teamId,
        canEdit: true,
        _original: member
      });
    });

    // Add waitlisted members (exclude those already shown as enrolled/active members)
    const memberStatuses = new Set(['Enrolled', 'Active', 'Suspended']);
    (members || []).filter(m => m.status === 'Waitlisted' || (m.canApplyDespiteRestrictions && !memberStatuses.has(m.status))).forEach(member => {
      result.push({
        id: member._id || member.userId,
        uniqueKey: `waitlist-${member._id || member.userId}`,
        type: 'waitlist',
        user: member.user || { _id: member.userId },
        role: null,
        status: member.status,
        source: 'waitlist',
        joinedAt: member.createdAt,
        canApplyDespiteRestrictions: member.canApplyDespiteRestrictions,
        canEdit: true,
        _original: member
      });
    });

    // Add applicants
    (applicants || []).forEach(applicant => {
      let parsedFormData = applicant.formData;
      if (typeof parsedFormData === 'string') {
        try { parsedFormData = JSON.parse(parsedFormData); } catch { parsedFormData = null; }
      }
      const personalInfo = parsedFormData?.personalInfo;
      const user = applicant.user ? { ...applicant.user } : { _id: applicant.userId };
      if (personalInfo) {
        if (!user.name && !user.firstName && (personalInfo.firstName || personalInfo.lastName)) {
          user.firstName = personalInfo.firstName;
          user.lastName = personalInfo.lastName;
          user.name = [personalInfo.firstName, personalInfo.lastName].filter(Boolean).join(' ');
        }
        if (!user.email && personalInfo.email) {
          user.email = personalInfo.email;
        }
      }

      result.push({
        id: applicant._id,
        uniqueKey: `applicant-${applicant._id}`,
        type: 'applicant',
        user,
        role: null,
        status: applicant.status || 'Pending',
        source: applicant.source || 'registration',
        joinedAt: applicant.submittedAt || applicant.createdAt,
        applicationNumber: applicant.applicationNumber,
        registrationApplicationNumber: applicant.registrationApplicationNumber,
        paidOfflineVerified: applicant.paid_offline_verified,
        canEdit: true,
        _original: applicant
      });
    });

    // Add followers (excluding those already in other lists)
    const existingUserIds = new Set(result.map(r => r.user?._id || r.id));
    (followers || []).forEach(follower => {
      const userId = follower.userId || follower.user?._id;
      if (!existingUserIds.has(userId)) {
        result.push({
          id: follower._id || userId,
          uniqueKey: `follower-${follower._id || userId}`,
          type: 'follower',
          user: follower.user || { _id: userId },
          role: null,
          status: 'Following',
          source: 'follower',
          joinedAt: follower.followedAt || follower.createdAt,
          membershipStatus: follower.membershipStatus,
          canEdit: false,
          _original: follower
        });
      }
    });

    return result;
  }, [members, applicants, followers, clubMembers]);

  // Apply filters
  const filteredData = useMemo(() => {
    return normalizedData.filter(person => {
      // Type filter
      if (filters.type !== 'all') {
        if (filters.type === 'members' && person.type !== 'member') return false;
        if (filters.type === 'staff' && person.type !== 'staff' && person.type !== 'club-management') return false;
        if (filters.type === 'applicants' && person.type !== 'applicant') return false;
        if (filters.type === 'waitlist' && person.type !== 'waitlist') return false;
        if (filters.type === 'followers' && person.type !== 'follower') return false;
      }

      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const userName = person.user?.name || person.user?.email || '';
        const userEmail = person.user?.email || '';
        if (!userName.toLowerCase().includes(searchLower) && 
            !userEmail.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      // Role filter
      if (filters.role && person.role !== filters.role) {
        return false;
      }

      // Status filter
      if (filters.status && person.status !== filters.status) {
        return false;
      }

      return true;
    });
  }, [normalizedData, filters]);

  // Calculate counts
  const counts = useMemo(() => {
    const membersData = (members || []).filter(m => m.status !== 'Waitlisted');
    const staffData = membersData.filter(m => !['Player', 'Observer'].includes(m.role));
    const waitlistData = (members || []).filter(m => m.status === 'Waitlisted' || m.canApplyDespiteRestrictions);
    
    return {
      total: normalizedData.length,
      members: membersData.length,
      staff: staffData.length + clubMembers.length,
      applicants: (applicants || []).length,
      pendingApplicants: (applicants || []).filter(a => 
        ['submitted', 'under_review', 'documents_pending', 'Pending'].includes(a.status)
      ).length,
      waitlist: waitlistData.length,
      followers: (followers || []).length,
      applications: (applicants || []).length
    };
  }, [normalizedData, members, applicants, followers, clubMembers]);

  // Get unique roles and statuses for filters
  const filterOptions = useMemo(() => {
    const roles = new Set();
    const statuses = new Set();
    
    normalizedData.forEach(person => {
      if (person.role) roles.add(person.role);
      if (person.status) statuses.add(person.status);
    });

    return {
      roles: Array.from(roles).sort(),
      statuses: Array.from(statuses).sort()
    };
  }, [normalizedData]);

  // Selection handlers
  const toggleSelection = useCallback((id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(filteredData.map(p => p.id)));
  }, [filteredData]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback((id) => selectedIds.has(id), [selectedIds]);

  // Refresh all data
  const refreshAll = useCallback(async () => {
    await Promise.all([
      refetchMembers(),
      refetchApplicants(),
      refetchFollowers(),
      fetchClubMembers()
    ]);
  }, [refetchMembers, refetchApplicants, refetchFollowers, fetchClubMembers]);

  // Loading state
  const isLoading = membersLoading || applicantsLoading || followersLoading || clubMembersLoading;

  // Error state
  const error = membersError || applicantsError;

  return {
    // Data
    data: filteredData,
    allData: normalizedData,
    counts,
    filterOptions,
    
    // Loading/Error
    isLoading,
    error,
    
    // Filters
    filters,
    setFilters,
    
    // Selection
    selectedIds: Array.from(selectedIds),
    selectedCount: selectedIds.size,
    toggleSelection,
    selectAll,
    clearSelection,
    isSelected,
    
    // Actions
    refreshAll,
    refetchMembers,
    refetchApplicants,
    refetchFollowers,
    
    // Mutations (pass through from existing hook)
    mutations
  };
};

export default usePeopleData;

